import { Box, Button, Card, CardContent, Chip, Collapse, Dialog, DialogContent, DialogTitle, FormControl,
    FormControlLabel, Grid, IconButton, InputBase, InputLabel, LinearProgress, MenuItem, Select, Slider, Stack, Switch,
    Tab, Tabs, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid } from "@mui/x-data-grid";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { tokens } from "../theme/theme";
import { UserContext } from "../theme/userContext";
import { fmtDate } from "./opportunities/opportunityFormat";
import {
    compareShortlistRank, fmtPeerTop, fmtQuarter, hasShortlist, normalizeViewMode, parseGateReport, qualityMessages,
    selectViewCandidates, toNumber, VIEW_ALL, VIEW_SHORTLIST,
} from "./screenerFormat";

const SIGNAL_COLORS = { GREEN: '#4caf50', YELLOW: '#ff9800', RED: '#f44336' };
const QUALITY_COLOR = '#ff9800';

const STRATEGY_TABS = [
    { key: 'growth', label: 'Growth' },
    { key: 'dividend', label: 'Dividende' },
    { key: 'longterm', label: 'Langfristig' },
];

const CATEGORY_LABELS = {
    valuation_score: 'Bewertung',
    growth_score: 'Wachstum',
    profitability_score: 'Profitabilität',
    balance_sheet_score: 'Bilanz',
    earnings_score: 'Earnings',
    analyst_score: 'Analysten',
    news_score: 'News',
    technical_score: 'Technicals',
    dividend_score: 'Dividende',
};

function SignalCard({ label, count, color, active, onClick }) {
    return (
        <Card onClick={onClick} sx={{
            backgroundColor: color, color: '#fff', minWidth: 110, cursor: 'pointer',
            border: active ? '3px solid #fff' : '3px solid transparent',
            opacity: active ? 1 : 0.75,
            transition: 'all 0.2s',
            '&:hover': { opacity: 1, transform: 'scale(1.03)' },
        }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{count}</Typography>
                <Typography variant="body1">{label}</Typography>
            </CardContent>
        </Card>
    );
}

function SignalDot({ signal }) {
    const color = SIGNAL_COLORS[signal] || '#9e9e9e';
    return <Box sx={{ width: 18, height: 18, borderRadius: '50%', backgroundColor: color, border: '2px solid rgba(255,255,255,0.3)' }} />;
}

function CategoryBar({ label, score }) {
    const missing = score == null;
    const value = missing ? 0 : Math.max(0, Math.min(100, Number(score)));
    const barColor = value >= 80 ? '#4caf50' : value >= 55 ? '#ff9800' : '#f44336';
    return (
        <Box sx={{ mb: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.3 }}>
                <Typography variant="body2" sx={{ opacity: missing ? 0.4 : 1, fontSize: '0.8rem' }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', opacity: missing ? 0.4 : 1, fontSize: '0.8rem' }}>
                    {missing ? '—' : Math.round(value)}
                </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={value} sx={{
                height: 6, borderRadius: 3,
                backgroundColor: 'rgba(128,128,128,0.2)',
                '& .MuiLinearProgress-bar': { backgroundColor: missing ? '#9e9e9e' : barColor, borderRadius: 3 },
            }} />
        </Box>
    );
}

function CandidateDetailDialog({ open, onClose, candidate }) {
    if (!candidate) return null;

    const trapFlags = candidate.trap_flags;
    const hasTraps = trapFlags && trapFlags.flags && trapFlags.flags.length > 0;
    const dataHints = qualityMessages(candidate.quality_flags);
    const hasPeerData = candidate.peer_group != null || toNumber(candidate.peer_rank_pct) != null || toNumber(candidate.absolute_score) != null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {candidate.name || candidate.identifier}
                </Typography>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Chip
                        label={`${candidate.signal} — ${fmt(candidate.final_score, 0)}`}
                        sx={{ backgroundColor: SIGNAL_COLORS[candidate.signal] || '#9e9e9e', color: '#fff', fontWeight: 'bold', fontSize: '1rem', height: 32 }}
                    />
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Asset Score: {fmt(candidate.asset_score, 0)} | Coverage: {candidate.coverage_pct != null ? `${Math.round(Number(candidate.coverage_pct))}%` : '—'}
                    </Typography>
                    {Number(candidate.coverage_penalty) !== 0 && (
                        <Typography variant="body2" sx={{ color: '#f44336' }}>
                            Coverage-Penalty: {Number(candidate.coverage_penalty).toFixed(1)}
                        </Typography>
                    )}
                    {Number(candidate.trap_penalty) !== 0 && (
                        <Typography variant="body2" sx={{ color: '#f44336' }}>
                            Trap-Penalty: {candidate.trap_penalty}
                        </Typography>
                    )}
                </Stack>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Teil-Scores</Typography>
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                            <CategoryBar key={key} label={label} score={candidate[key]} />
                        ))}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Kennzahlen</Typography>
                        <MetricRow label="Kurs" value={fmt(candidate.price, 2)} />
                        <MetricRow label="Kursdatum" value={fmtDate(candidate.price_date)} />
                        <MetricRow label="Quartalsdatum" value={fmtDate(candidate.fundamentals_date)} />
                        <MetricRow label="Market Cap (Mln)" value={fmt(candidate.market_cap_mln, 0)} />
                        <MetricRow label="KGV" value={fmt(candidate.pe_ratio, 1)} />
                        <MetricRow label="Div.-Rendite" value={candidate.dividend_yield != null ? (Number(candidate.dividend_yield) * 100).toFixed(2) + '%' : '—'} />
                        <MetricRow label="Umsatzwachstum YoY" value={candidate.revenue_growth_yoy != null ? (Number(candidate.revenue_growth_yoy) * 100).toFixed(1) + '%' : '—'} />
                        <MetricRow label="Target Upside" value={candidate.target_upside_pct != null ? Number(candidate.target_upside_pct).toFixed(1) + '%' : '—'} />
                        <MetricRow label="Analyst Rating" value={fmt(candidate.analyst_rating, 1)} />
                        <MetricRow label="52w Position" value={candidate.pos_in_52w_range != null ? (Number(candidate.pos_in_52w_range) * 100).toFixed(0) + '%' : '—'} />
                        <MetricRow label="vs EMA200" value={candidate.pct_vs_ema200 != null ? Number(candidate.pct_vs_ema200).toFixed(1) + '%' : '—'} />

                        {hasPeerData && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>Peer-Vergleich</Typography>
                                <MetricRow label="Peer-Gruppe" value={candidate.peer_group || '—'} />
                                <MetricRow label="Perzentil" value={fmtPeerTop(candidate.peer_rank_pct)} />
                                <MetricRow label="Score vor Peer-Vergleich" value={fmt(candidate.absolute_score, 0)} />
                            </Box>
                        )}

                        {dataHints.length > 0 && (
                            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(255,152,0,0.1)', border: '1px solid rgba(255,152,0,0.4)' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    <WarningAmberIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom', color: QUALITY_COLOR }} />
                                    Datenhinweise
                                </Typography>
                                {dataHints.map((message, i) => (
                                    <Typography key={i} variant="body2" sx={{ opacity: 0.9 }}>{message}</Typography>
                                ))}
                            </Box>
                        )}

                        {hasTraps && (
                            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)' }}>
                                <Typography variant="subtitle2" sx={{ color: '#f44336', fontWeight: 'bold', mb: 0.5 }}>
                                    <WarningAmberIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                                    Trap-Warnung
                                </Typography>
                                {trapFlags.reasons.map((r, i) => (
                                    <Typography key={i} variant="body2" sx={{ opacity: 0.9 }}>{r}</Typography>
                                ))}
                            </Box>
                        )}
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button variant="outlined" component={Link}
                        to={{ pathname: "/detailview" }}
                        state={{ identifier: candidate.identifier, strategy: candidate.strategy }}
                    >
                        Zur Detailansicht
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

function MetricRow({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>{label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value}</Typography>
        </Stack>
    );
}

function GateReasonList({ title, entries, emptyText }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{title}</Typography>
            {entries.length === 0 && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>{emptyText}</Typography>
            )}
            {entries.map(entry => (
                <MetricRow key={entry.code} label={entry.label} value={fmt(entry.count, 0)} />
            ))}
        </Box>
    );
}

function fmt(val, decimals) {
    if (val == null) return '—';
    return Number(val).toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/**
 * StrategyCandidates — Strategy-based candidate screener panel.
 * Rendered as a tab inside the main Stocks/Screener page.
 */
const StrategyCandidates = () => {
    const { user } = useContext(UserContext);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Restore filter state from sessionStorage
    const savedFilters = () => {
        try {
            const raw = sessionStorage.getItem('screener_filters');
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    };
    const sf = savedFilters();

    const [strategyIdx, setStrategyIdx] = useState(sf.strategyIdx ?? 0);
    const [candidates, setCandidates] = useState([]);
    const [summary, setSummary] = useState({});
    const [sectors, setSectors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [signalFilter, setSignalFilter] = useState(sf.signalFilter ?? 'ALL');
    const [sectorFilter, setSectorFilter] = useState(sf.sectorFilter ?? '');
    const [countryFilter, setCountryFilter] = useState(sf.countryFilter ?? '');
    const [minScoreFilter, setMinScoreFilter] = useState(sf.minScoreFilter ?? 0);
    const [minMcapFilter, setMinMcapFilter] = useState(sf.minMcapFilter ?? 0);
    const [searchText, setSearchText] = useState(sf.searchText ?? '');
    const [favoritesOnly, setFavoritesOnly] = useState(sf.favoritesOnly ?? false);
    const [viewMode, setViewMode] = useState(normalizeViewMode(sf.viewMode));
    const [sortModels, setSortModels] = useState({
        [VIEW_SHORTLIST]: [{ field: 'shortlist_rank', sort: 'asc' }],
        [VIEW_ALL]: [{ field: 'final_score', sort: 'desc' }],
    });
    const [gateOpen, setGateOpen] = useState(false);
    const [favoriteSet, setFavoriteSet] = useState(new Set());
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState(null);

    const strategy = STRATEGY_TABS[strategyIdx].key;
    const base = process.env.REACT_APP_API_URI;
    const pUser = user?.p_user;

    // Persist filter state to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('screener_filters', JSON.stringify({
            strategyIdx, signalFilter, sectorFilter, countryFilter,
            minScoreFilter, minMcapFilter, searchText, favoritesOnly, viewMode,
        }));
    }, [strategyIdx, signalFilter, sectorFilter, countryFilter, minScoreFilter, minMcapFilter, searchText, favoritesOnly, viewMode]);

    useEffect(() => {
        fetch(`${base}/api/screener/summary`)
            .then(r => r.json())
            .then(data => setSummary(data))
            .catch(console.error);
    }, [base]);

    // Load candidates, sectors, and favorites when strategy changes
    useEffect(() => {
        setLoading(true);

        // Top 200 plus Shortlist-Papiere außerhalb davon, siehe Limit der API.
        const params = new URLSearchParams({ strategy, limit: '250' });
        const fetches = [
            fetch(`${base}/api/screener/candidates?${params}`).then(r => r.json()),
            fetch(`${base}/api/screener/sectors?strategy=${strategy}`).then(r => r.json()),
        ];
        if (pUser) {
            fetches.push(
                fetch(`${base}/api/screener/favorites?puser=${pUser}&strategy=${strategy}`).then(r => r.json())
            );
        }

        Promise.all(fetches).then(([cands, sects, favs]) => {
            setCandidates(Array.isArray(cands) ? cands : []);
            setSectors(Array.isArray(sects) ? sects : []);
            if (Array.isArray(favs)) {
                setFavoriteSet(new Set(favs.map(f => f.identifier)));
            }
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    }, [strategy, base, pUser]);

    const stratSummary = summary[strategy] || {};
    const gateReport = parseGateReport(stratSummary.gate_report);

    const handleToggleFavorite = async (e, identifier) => {
        e.stopPropagation();
        if (!pUser) return;

        try {
            const response = await fetch(`${base}/api/screener/favorite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ puser: pUser, identifier, strategy }),
            });
            if (!response.ok) {
                const errorBody = await response.json().catch(() => ({}));
                throw new Error(errorBody.message || 'toggle favorite failed');
            }
            const data = await response.json();
            setFavoriteSet(prev => {
                const next = new Set(prev);
                if (data.favorite) {
                    next.add(identifier);
                } else {
                    next.delete(identifier);
                }
                return next;
            });
        } catch (err) {
            console.error('Toggle favorite failed:', err);
        }
    };

    // Derive country list from loaded candidates
    const countries = [...new Set(candidates.map(c => c.countryname).filter(Boolean))].sort();

    // Der gespeicherte Wunsch bleibt erhalten, gilt aber nur, wenn der Lauf eine Shortlist hat.
    const shortlistAvailable = hasShortlist(candidates);
    const activeView = shortlistAvailable ? viewMode : VIEW_ALL;
    const viewCandidates = selectViewCandidates(candidates, activeView);

    const searchLower = searchText.toLowerCase().trim();
    const filtered = viewCandidates.filter(c => {
        if (signalFilter !== 'ALL' && c.signal !== signalFilter) return false;
        if (sectorFilter && c.sector !== sectorFilter) return false;
        if (countryFilter && c.countryname !== countryFilter) return false;
        if (minScoreFilter > 0 && Number(c.final_score) < minScoreFilter) return false;
        if (minMcapFilter > 0 && (Number(c.market_cap_mln) || 0) < minMcapFilter) return false;
        if (favoritesOnly && !favoriteSet.has(c.identifier)) return false;
        if (searchLower && !(
            (c.identifier || '').toLowerCase().includes(searchLower) ||
            (c.name || '').toLowerCase().includes(searchLower) ||
            (c.sector || '').toLowerCase().includes(searchLower)
        )) return false;
        return true;
    });

    const hasActiveFilters = signalFilter !== 'ALL' || sectorFilter || countryFilter || minScoreFilter > 0 || minMcapFilter > 0 || searchText || favoritesOnly;

    const handleRowClick = (params) => {
        setSelectedCandidate(params.row);
        setDetailOpen(true);
    };

    const columns = [
        {
            field: 'favorite', headerName: '', width: 45, sortable: false, disableColumnMenu: true,
            renderCell: (params) => {
                const isFav = favoriteSet.has(params.row.identifier);
                return (
                    <IconButton size="small" onClick={(e) => handleToggleFavorite(e, params.row.identifier)}
                        sx={{ p: 0.5 }}>
                        {isFav
                            ? <StarIcon sx={{ fontSize: 20, color: '#ffc107' }} />
                            : <StarBorderIcon sx={{ fontSize: 20, opacity: 0.3 }} />
                        }
                    </IconButton>
                );
            },
        },
        {
            field: 'signal', headerName: 'Signal', width: 65, align: 'center', headerAlign: 'center',
            renderCell: (params) => <SignalDot signal={params.value} />,
        },
        ...(shortlistAvailable ? [{
            field: 'shortlist_rank', headerName: 'Rang', width: 75, type: 'number',
            sortComparator: compareShortlistRank,
            valueFormatter: (params) => params.value != null ? params.value : '',
        }] : []),
        {
            field: 'identifier', headerName: 'Identifier', width: 115,
            renderCell: (params) => (
                <Button color="inherit" variant="text" size="small" component={Link}
                    to={{ pathname: "/detailview" }}
                    state={{ identifier: params.row.identifier, strategy: params.row.strategy }}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                >{params.value}</Button>
            ),
        },
        { field: 'name', headerName: 'Name', width: 150 },
        {
            field: 'final_score', headerName: 'Score', width: 75, type: 'number',
            renderCell: (params) => {
                if (params.value == null) return '';
                const score = Number(params.value);
                const color = SIGNAL_COLORS[params.row.signal] || '#9e9e9e';
                return <Chip label={Math.round(score)} size="small" sx={{ backgroundColor: color, color: '#fff', fontWeight: 'bold' }} />;
            },
        },
        {
            // Sortiert wird nach dem Rohwert: Der erste Klick stellt die Besten nach oben, leere nach unten.
            field: 'peer_rank_pct', headerName: 'Peer', width: 80, type: 'number',
            sortingOrder: ['desc', 'asc', null],
            valueGetter: (params) => toNumber(params.value),
            renderCell: (params) => (
                <Tooltip title={params.row.peer_group || ''} arrow>
                    <Typography variant="body2">{fmtPeerTop(params.value)}</Typography>
                </Tooltip>
            ),
        },
        {
            field: 'fundamentals_date', headerName: 'Daten', width: 70,
            sortingOrder: ['desc', 'asc', null],
            renderCell: (params) => {
                const { price_date: priceDate, fundamentals_date: fundamentalsDate } = params.row;
                const title = priceDate || fundamentalsDate
                    ? <><div>Kurs: {fmtDate(priceDate)}</div><div>Quartal: {fmtDate(fundamentalsDate)}</div></>
                    : '';
                return (
                    <Tooltip title={title} arrow>
                        <Typography variant="body2">{fmtQuarter(fundamentalsDate)}</Typography>
                    </Tooltip>
                );
            },
        },
        {
            field: 'quality_flags', headerName: 'Datenhinweise', width: 45, sortable: false, filterable: false,
            align: 'center', headerAlign: 'center',
            renderHeader: () => (
                <Tooltip title="Datenhinweise" arrow>
                    <WarningAmberIcon sx={{ fontSize: 18, color: QUALITY_COLOR, verticalAlign: 'middle' }} />
                </Tooltip>
            ),
            renderCell: (params) => {
                const messages = qualityMessages(params.value);
                if (messages.length === 0) return null;
                return (
                    <Tooltip title={messages.map((message, i) => <div key={i}>{message}</div>)} arrow>
                        <WarningAmberIcon aria-label="Datenhinweise" sx={{ color: QUALITY_COLOR, fontSize: 18 }} />
                    </Tooltip>
                );
            },
        },
        {
            field: 'trap_flags', headerName: 'Trap', width: 55, sortable: false,
            renderCell: (params) => {
                const flags = params.value;
                if (!flags || !flags.flags || flags.flags.length === 0) return null;
                return (
                    <Tooltip title={flags.reasons?.join('\n') || flags.flags.join(', ')} arrow>
                        <WarningAmberIcon sx={{ color: '#f44336', fontSize: 18 }} />
                    </Tooltip>
                );
            },
        },
        {
            field: 'coverage_pct', headerName: 'Cov.', width: 60, type: 'number',
            valueFormatter: (params) => params.value != null ? Math.round(Number(params.value)) + '%' : '',
        },
        { field: 'sector', headerName: 'Sektor', width: 120 },
        { field: 'countryname', headerName: 'Land', width: 80 },
        {
            field: 'market_cap_mln', headerName: 'MCap (M)', width: 85, type: 'number',
            valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString('de-DE', { maximumFractionDigits: 0 }) : '',
        },
        {
            field: 'pe_ratio', headerName: 'KGV', width: 70, type: 'number',
            valueFormatter: (params) => params.value != null ? Number(params.value).toFixed(1) : '',
        },
        ...(strategy === 'dividend' ? [{
            field: 'dividend_yield', headerName: 'Div.%', width: 70, type: 'number',
            valueFormatter: (params) => params.value != null ? (Number(params.value) * 100).toFixed(2) : '',
        }] : []),
        {
            field: 'revenue_growth_yoy', headerName: 'Rev.YoY', width: 75, type: 'number',
            renderCell: (params) => {
                if (params.value == null) return '';
                const pct = Number(params.value) * 100;
                const color = pct > 0 ? '#4caf50' : pct < 0 ? '#f44336' : 'inherit';
                return <Typography variant="body2" sx={{ color }}>{pct.toFixed(1)}%</Typography>;
            },
        },
        {
            field: 'target_upside_pct', headerName: 'Target%', width: 75, type: 'number',
            renderCell: (params) => {
                if (params.value == null) return '';
                const val = Number(params.value);
                const color = val > 0 ? '#4caf50' : '#f44336';
                return <Typography variant="body2" sx={{ color }}>{val.toFixed(1)}%</Typography>;
            },
        },
        {
            field: 'pos_in_52w_range', headerName: '52w', width: 60, type: 'number',
            valueFormatter: (params) => params.value != null ? (Number(params.value) * 100).toFixed(0) + '%' : '',
        },
    ];

    const favCount = viewCandidates.filter(c => favoriteSet.has(c.identifier)).length;

    return (
        <Box>
            {/* Strategy Sub-Tabs */}
            <Tabs value={strategyIdx} onChange={(e, v) => setStrategyIdx(v)} textColor="secondary" indicatorColor="secondary" sx={{ mb: 2 }}>
                {STRATEGY_TABS.map((t) => (
                    <Tab key={t.key} label={t.label} />
                ))}
            </Tabs>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item>
                    <SignalCard label="Grün" count={stratSummary.green || 0} color="#4caf50"
                        active={signalFilter === 'GREEN'} onClick={() => setSignalFilter(f => f === 'GREEN' ? 'ALL' : 'GREEN')} />
                </Grid>
                <Grid item>
                    <SignalCard label="Gelb" count={stratSummary.yellow || 0} color="#ff9800"
                        active={signalFilter === 'YELLOW'} onClick={() => setSignalFilter(f => f === 'YELLOW' ? 'ALL' : 'YELLOW')} />
                </Grid>
                <Grid item>
                    <SignalCard label="Rot" count={stratSummary.red || 0} color="#f44336"
                        active={signalFilter === 'RED'} onClick={() => setSignalFilter(f => f === 'RED' ? 'ALL' : 'RED')} />
                </Grid>
                <Grid item>
                    <Card onClick={() => setSignalFilter('ALL')} sx={{
                        backgroundColor: theme.palette.mode === 'dark' ? colors.primary[400] : colors.primary[100],
                        minWidth: 110, cursor: 'pointer',
                        border: signalFilter === 'ALL' ? '3px solid #2196f3' : '3px solid transparent',
                        transition: 'all 0.2s',
                        '&:hover': { transform: 'scale(1.03)' },
                    }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stratSummary.total || 0}</Typography>
                            <Typography variant="body1">Gesamt</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {stratSummary.finished_at && (
                    <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                Lauf vom {new Date(stratSummary.finished_at).toLocaleString('de-DE')}
                                {!gateReport && stratSummary.universe_count != null && ` (${stratSummary.universe_count} geprüft)`}
                            </Typography>
                            {gateReport && (
                                <Button size="small" color="inherit" onClick={() => setGateOpen(o => !o)} aria-expanded={gateOpen}
                                    endIcon={<ExpandMoreIcon sx={{ transform: gateOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                                    sx={{ textTransform: 'none', p: 0, mt: 0.5 }}
                                >
                                    Geprüft: {fmt(gateReport.after, 0)} von {fmt(gateReport.before, 0)} Papieren
                                </Button>
                            )}
                        </Box>
                    </Grid>
                )}
            </Grid>

            {/* Prüfbericht */}
            {gateReport && (
                <Collapse in={gateOpen} unmountOnExit>
                    <Box sx={{
                        mb: 2, p: 2, borderRadius: 1,
                        backgroundColor: theme.palette.mode === 'dark' ? colors.primary[400] : colors.primary[100],
                    }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <GateReasonList title="Ausgeschlossene Papiere" entries={gateReport.excluded} emptyText="Keine Ausschlüsse" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <GateReasonList title="Verworfene Werte" entries={gateReport.voided} emptyText="Keine verworfenen Werte" />
                                {gateReport.scoringErrors > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <MetricRow label="Fehler bei der Bewertung" value={fmt(gateReport.scoringErrors, 0)} />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            )}

            {/* Filter Bar */}
            <Stack direction="row" spacing={2} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
                {/* Shortlist oder alle */}
                <ToggleButtonGroup exclusive size="small" color="secondary" value={activeView}
                    onChange={(e, value) => { if (value) setViewMode(value); }}
                    aria-label="Ansicht der Kandidaten"
                >
                    <ToggleButton value={VIEW_SHORTLIST} disabled={!shortlistAvailable} sx={{ textTransform: 'none' }}>Shortlist</ToggleButton>
                    <ToggleButton value={VIEW_ALL} sx={{ textTransform: 'none' }}>Alle Kandidaten</ToggleButton>
                </ToggleButtonGroup>
                {!loading && candidates.length > 0 && !shortlistAvailable && (
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>Dieser Lauf enthält keine Shortlist</Typography>
                )}
                {/* Suchfeld */}
                <Box sx={{
                    display: 'flex', alignItems: 'center',
                    backgroundColor: theme.palette.mode === 'dark' ? colors.primary[400] : colors.primary[100],
                    borderRadius: '8px', height: 36, px: 1.5, minWidth: 200,
                }}>
                    <SearchIcon sx={{ fontSize: 18, opacity: 0.5, mr: 0.5 }} />
                    <InputBase
                        placeholder="Name, Identifier..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{ flex: 1, fontSize: '0.85rem' }}
                        inputProps={{ spellCheck: false }}
                    />
                    {searchText && (
                        <IconButton size="small" onClick={() => setSearchText('')} sx={{ p: 0.3 }}>
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </Box>
                {/* Favoriten */}
                <FormControlLabel
                    control={
                        <Switch checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)}
                            size="small" color="warning" />
                    }
                    label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <StarIcon sx={{ fontSize: 18, color: favoritesOnly ? '#ffc107' : 'rgba(255,255,255,0.3)' }} />
                            <Typography variant="body2">Favoriten ({favCount})</Typography>
                        </Stack>
                    }
                    sx={{ mr: 0 }}
                />
                {/* Sektor */}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Sektor</InputLabel>
                    <Select value={sectorFilter} label="Sektor" onChange={(e) => setSectorFilter(e.target.value)}>
                        <MenuItem value="">Alle Sektoren</MenuItem>
                        {sectors.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                </FormControl>
                {/* Land */}
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Land</InputLabel>
                    <Select value={countryFilter} label="Land" onChange={(e) => setCountryFilter(e.target.value)}>
                        <MenuItem value="">Alle Länder</MenuItem>
                        {countries.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                </FormControl>
                {/* Min Score */}
                <Box sx={{ width: 160 }}>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>Min Score: {minScoreFilter || '—'}</Typography>
                    <Slider size="small" value={minScoreFilter} onChange={(e, v) => setMinScoreFilter(v)}
                        min={0} max={80} step={5} valueLabelDisplay="auto" />
                </Box>
                {/* Min MCap */}
                <Box sx={{ width: 180 }}>
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                        Min MCap: {minMcapFilter > 0 ? `${(minMcapFilter / 1000).toFixed(0)} Mrd.` : '—'}
                    </Typography>
                    <Slider size="small" value={minMcapFilter} onChange={(e, v) => setMinMcapFilter(v)}
                        min={0} max={50000} step={1000} valueLabelDisplay="auto"
                        valueLabelFormat={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}B` : `${v}M`} />
                </Box>
                {/* Active filter indicator */}
                {hasActiveFilters && (
                    <Chip label={`${filtered.length} von ${viewCandidates.length}`} size="small" variant="outlined"
                        onDelete={() => {
                            setSignalFilter('ALL'); setSectorFilter(''); setCountryFilter('');
                            setMinScoreFilter(0); setMinMcapFilter(0); setSearchText(''); setFavoritesOnly(false);
                        }} />
                )}
            </Stack>

            {/* DataGrid */}
            {loading ? (
                <Typography>Laden...</Typography>
            ) : candidates.length === 0 ? (
                <Typography sx={{ opacity: 0.6, mt: 2 }}>
                    Keine Strategie-Kandidaten verfügbar. Der Screener-Lauf muss zuerst ausgeführt werden.
                </Typography>
            ) : activeView === VIEW_SHORTLIST && filtered.length === 0 ? (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    <Typography sx={{ opacity: 0.7 }}>Kein Papier der Shortlist passt zu den Filtern.</Typography>
                    <Button size="small" variant="outlined" color="inherit" onClick={() => setViewMode(VIEW_ALL)}>Alle Kandidaten anzeigen</Button>
                </Stack>
            ) : (
                <DataGrid
                    rows={filtered.map(r => ({ ...r, id: r.id || r.identifier }))}
                    columns={columns}
                    autoHeight
                    density="compact"
                    sortModel={sortModels[activeView]}
                    onSortModelChange={(model) => setSortModels(prev => ({ ...prev, [activeView]: model }))}
                    pageSizeOptions={[25, 50, 100, 200]}
                    onRowClick={handleRowClick}
                    sx={{
                        cursor: 'pointer',
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                        },
                    }}
                />
            )}

            <CandidateDetailDialog
                open={detailOpen}
                onClose={() => setDetailOpen(false)}
                candidate={selectedCandidate}
            />
        </Box>
    );
};

export default StrategyCandidates;
